import React, { useContext, useEffect, useState } from "react";
import { getWorkpaperTemplates, updateWorkpaperTemplates } from "../../apiClient/operations/workpapersOperations";
import Table from "../../tailwindUI/Table";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { PaperClipIcon } from '@heroicons/react/20/solid';
import PrimaryButton from "../../tailwindUI/PrimaryButton";
import { AlertContext } from "../../context/AlertContext";
import EmptyState from "../../tailwindUI/EmptyState";
import SlideOver from "../../tailwindUI/SlideOver";
import SecondaryLoader from "../../hooks/SecondaryLoader";
import LongAlert from "../../tailwindUI/LongAlert";
import { cn } from "../../util";
import DocumentList from "../../tailwindUI/DocumentList";
import FileInput from "../../tailwindUI/FileInput";
import { uploadS3 } from "../../apiClient/operations/s3UploadOperations";

const columns = [
    { heading: "Nombre", value: "name", main: true },
    { heading: "Tipo", value: "type" },
    { heading: "Regímenes", value: "regime_id" }
];

function WorkpaperTemplates() {
    const [workpaperTemplates, setWorkpaperTemplates] = useState([]);
    const [templateToUpdate, setTemplateToUpdate] = useState(null);
    const [openSlideForm, setOpenSlideForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState(null);
    const [fileToUpdate, setFileToUpdate] = useState(null);
    const { setAlert } = useContext(AlertContext);

    const columnActions = [
        {
            id: 1,
            name: "Editar",
            type: "secondary",
            icon: <PencilSquareIcon className="w-5 h-5 text-gray-600" />,
            action: (item) => {
                setOpenSlideForm(true);
                const templateToUpdate = workpaperTemplates.find((template) => template.id === item.id);
                setTemplateToUpdate(templateToUpdate);
            },
        },
    ];

    useEffect(() => {
        const getTemplates = async () => {
            try {
                setIsLoading(true);
                const response = await getWorkpaperTemplates();
                setWorkpaperTemplates(response?.data || []);
            } catch (error) {
                console.error("Error fetching payment settings:", error);
            } finally {
                setIsLoading(false);
            }
        };
        getTemplates();
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsPending(true);
            const name = e.target.elements.template_name?.value;
            const regime = e.target.elements.regime_id?.value;
            const type = e.target.elements.type?.value;
            let err = {};

            if (!(/^[A-Za-zÁÉÍÓÚáéíóúÜüÑñ,./ -']{4,45}$/.test(name))) {
                err.template_name = "Ingresa el nombre";
            }
            if (!(/^\d{3}(\|\d{3})*$/.test(regime))) {
                err.regime_id = "Ingresa el o los regímenes separados por '|'";
            }
            if (!(/^[A-Za-zÁÉÍÓÚáéíóúÜüÑñ,./ -']{2,45}$/.test(type))) {
                err.type = "Ingresa el tipo";
            }

            if (Object.keys(err).length > 0) {
                console.log("err:", err)
                setErrors(err);
                return;
            }

            let params = {
                "name": name,
                "regime_id": regime,
                "type": type
            }
            if (fileToUpdate) {
                const formData = new FormData();
                const path = `workpaper_templates/asf/pending/${fileToUpdate?.name}`
                formData.set("file", fileToUpdate, path);
                const { Bucket, Key } = await uploadS3(formData);
                params.document = {
                    "bucket": Bucket,
                    "path": Key
                }
            }

            const workpaper_tempalte = await updateWorkpaperTemplates(templateToUpdate?.id, params)

            setWorkpaperTemplates(workpaperTemplates.map(template => {
                if (template.id === workpaper_tempalte.id) {
                    return workpaper_tempalte;
                }
                return template;
            }))

            onClose();
            setAlert({
                active: true,
                type: "success",
                message: "Template actualizado correctamente"
            });
        } catch (error) {
            setError(error?.message || "Error al procesar tu solicitud");
        } finally {
            setIsPending(false);
        }
    };

    const onClose = () => {
        setTemplateToUpdate(null);
        setErrors(null);
        setOpenSlideForm(false);
        setTimeout(() => {
            setFileToUpdate(null);
        }, 500)
    };

    return (
        <>
            {isPending && <SecondaryLoader />}
            <div className="w-full h-full">
                <div className="w-full xs:h-full md:h-full">
                    <div className="mt-10 md:mt-0 w-full py-5 xs:h-auto md:h-1/12 hidden md:flex">
                        <div className="flex justify-between items-center w-full">
                            <h1 className="text-4xl font-bold text-left">
                                Templates de Papeles de Trabajo
                            </h1>
                            {/*<PrimaryButton
                                type="button"
                                onClick={() => {}}
                                disabled={isLoading}
                            >
                                Crear template
                            </PrimaryButton>*/}
                        </div>
                    </div>
                    {workpaperTemplates?.length > 0 || isLoading ? (
                        <Table
                            title="Templates de papeles de trabajo"
                            columns={columns}
                            data={workpaperTemplates}
                            actions={columnActions}
                            isLoadingData={isLoading}
                        />
                    ) : (
                        <EmptyState
                            title="No se encontraron templates"
                            text="Agrega un nuevo template para comenzar"
                        />
                    )}

                    <SlideOver
                        open={openSlideForm}
                        setOpen={setOpenSlideForm}
                        onClose={onClose}
                        title={"Actualizar template"}
                        className="max-h-screen"
                    >
                        <form
                            onSubmit={onSubmit}
                            className="w-full h-full flex flex-col gap-2"
                        >
                            {error && (
                                <div className="w-full sticky mb-24 z-[30]">
                                    <LongAlert
                                        className="px-0"
                                        title={error}
                                        show={error !== null}
                                        onClose={() => setError(null)}
                                        alwaysShow
                                    />
                                </div>
                            )}
                            <div>
                                <label
                                    htmlFor="template_name"
                                    className="block text-sm font-medium text-gray-500"
                                >
                                    Nombre
                                </label>
                                <input
                                    id="template_name"
                                    type="text"
                                    placeholder="Nombre del template"
                                    defaultValue={templateToUpdate?.name || ""}
                                    className={cn(
                                        "font-normal border-v2-gray-border-tables text-v2-text-bar-steps focus:ring-v2-blue-text-login focus:border-v2-blue-text-login transition-all block w-full px-4 my-1 rounded-md disabled:cursor-not-allowed",
                                        errors?.template_name && "border-red-500"
                                    )}
                                />
                                <p
                                    role="alert"
                                    className={cn(
                                        "mt-1 text-xs text-red-500",
                                        errors?.template_name ? "visible" : "invisible"
                                    )}
                                >
                                    {errors?.template_name}
                                </p>
                            </div>
                            <div>
                                <label
                                    htmlFor="regime_id"
                                    className="block text-sm font-medium text-gray-500"
                                >
                                    Regímenes
                                </label>
                                <input
                                    id="regime_id"
                                    type="text"
                                    placeholder="Regímenes"
                                    defaultValue={templateToUpdate?.regime_id || ""}
                                    className={cn(
                                        "font-normal border-v2-gray-border-tables text-v2-text-bar-steps focus:ring-v2-blue-text-login focus:border-v2-blue-text-login transition-all block w-full px-4 my-1 rounded-md disabled:cursor-not-allowed",
                                        errors?.regime_id && "border-red-500"
                                    )}
                                />
                                <p
                                    role="alert"
                                    className={cn(
                                        "mt-1 text-xs text-red-500",
                                        errors?.regime_id ? "visible" : "invisible"
                                    )}
                                >
                                    {errors?.regime_id}
                                </p>
                            </div>
                            <div>
                                <label
                                    htmlFor="type"
                                    className="block text-sm font-medium text-gray-500"
                                >
                                    Tipo
                                </label>
                                <input
                                    id="type"
                                    type="text"
                                    placeholder="Tipo"
                                    defaultValue={templateToUpdate?.type || ""}
                                    className={cn(
                                        "font-normal border-v2-gray-border-tables text-v2-text-bar-steps focus:ring-v2-blue-text-login focus:border-v2-blue-text-login transition-all block w-full px-4 my-1 rounded-md disabled:cursor-not-allowed",
                                        errors?.type && "border-red-500"
                                    )}
                                />
                                <p
                                    role="alert"
                                    className={cn(
                                        "mt-1 text-xs text-red-500",
                                        errors?.type ? "visible" : "invisible"
                                    )}
                                >
                                    {errors?.type}
                                </p>
                            </div>
                            <div>
                                <label
                                    htmlFor="document"
                                    className="block text-sm font-medium text-gray-500"
                                >
                                    Documento
                                </label>
                                {fileToUpdate ? (
                                    <div className="w-full text-sm font-medium text-gray-700">
                                        <div className="border border-gray-200 rounded-md pl-3 pr-4 py-3 flex items-center justify-between">
                                            <div className="w-0 flex-1 flex items-center">
                                                <PaperClipIcon className='flex-shrink-0 h-5 w-5 text-gray-400' />
                                                <span className="ml-2 flex-1 w-0 truncate">{fileToUpdate?.name}</span>
                                            </div>
                                            <div className="ml-4 flex-shrink-0">
                                                <a onClick={() => setFileToUpdate(null)} className="cursor-pointer transition-all font-medium text-orange">Eliminar</a>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <DocumentList
                                            documents={[templateToUpdate?.document].map(document => {
                                                const documentName = document?.path?.split("/")?.pop();
                                                return {
                                                    bucket: document?.bucket,
                                                    key: document?.path,
                                                    name: documentName
                                                }
                                            })}
                                            onError={(e) => setAlert({ active: true, type: 'error', message: e.message })} />
                                        <div className="mt-6">
                                            <FileInput
                                                id='new_document'
                                                accept={['.xlsx']}
                                                limitSize={1}
                                                limitFiles={1}
                                                setFiles={(files) => setFileToUpdate(files[0])}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="flex-1 h-full w-full flex items-end justify-end pb-6">
                                <div className="w-[15rem]">
                                    <PrimaryButton type="submit" isFullWidth>
                                        Actualizar template
                                    </PrimaryButton>
                                </div>
                            </div>
                        </form>
                    </SlideOver>
                </div>
            </div>
        </>
    );
}

export default WorkpaperTemplates;